/**
 * SurfaceHeader — SSOT for the chrome row at the top of every module.
 *
 * Every authenticated surface in `src/modules/*` renders this as its
 * first element so the app reads as one product no matter where the
 * user is:
 *   - Same 64px height, same padding, same border-bottom rule.
 *   - Mobile hamburger (sidebar trigger) lives in the exact same slot.
 *   - Title typography + truncation behaviour are uniform.
 *   - Optional eyebrow, subtitle, back button, and breadcrumb slots
 *     cover dashboard / list / detail / settings / multi-step patterns.
 *   - `actions` slot on the right for primary CTAs, notification bells,
 *     filter toggles, etc.
 *
 * Page-type recipes:
 *   - Dashboard:   title + subtitle? + actions
 *   - List:        title + actions (search/create)
 *   - Detail:      back + title
 *   - Nested:      breadcrumb + title
 *   - Settings:    title + subtitle?
 *   - Messaging:   back (mobile) + title + count?
 *
 * Presentational — all behaviour lives in the caller.
 */

import type { ReactNode } from 'react';
import { PanelLeft, ArrowLeft } from 'lucide-react';
import Breadcrumb, { type BreadcrumbItem } from '../ui/Breadcrumb';
import NotificationsBell from '../../modules/notifications/NotificationsBell/NotificationsBell';
import { NetworkIndicator } from '../network/NetworkIndicator';
import FocusChip from './FocusChip/FocusChip';
// SurfaceHeader.css is imported globally from main.tsx so the
// cross-surface normaliser sits early in the cascade and beats every
// module's own header CSS.

export type SurfaceHeaderProps = {
    /** The title rendered at the top-left of every surface. Required
     *  unless a breadcrumb is provided. */
    title?: ReactNode;
    /** Small mono eyebrow rendered above the title. Use for
     *  section-context (`Admin`, `Coaching`, `Settings`). */
    eyebrow?: ReactNode;
    /** Muted second line under the title. Short description / status. */
    subtitle?: ReactNode;
    /** Optional leading icon next to the title. */
    icon?: ReactNode;
    /** Shell hamburger trigger — shown only on ≤820px viewports
     *  (matches `--bp-md`, the canonical layout-switching breakpoint). */
    onMenuClick?: (() => void) | undefined;
    /** Gate hamburger visibility. Defaults to true. */
    showMenu?: boolean | undefined;
    /** Detail-page back action — replaces the hamburger when present. */
    onBack?: (() => void) | undefined;
    /** Accessible label for the back button (defaults to "Back"). */
    backLabel?: string | undefined;
    /** Breadcrumb trail — rendered in place of the bare title when
     *  provided. Last item is the current page and is not clickable. */
    breadcrumb?: BreadcrumbItem[] | undefined;
    /** Small count chip shown right after the title. */
    count?: ReactNode | undefined;
    /** Right-aligned action slot (primary button, icon buttons, etc.).
     *  Rendered to the LEFT of the notifications bell so caller
     *  actions and the global bell coexist. */
    actions?: ReactNode | undefined;
    /** Hide the auto-rendered network indicator + notifications bell.
     *  Default false — every surface carries the bell so users can
     *  reach notifications from anywhere. Only opt out on surfaces
     *  that already surface notifications inline. */
    hideNotifications?: boolean | undefined;
    /** Hide the global FocusChip (WS-NAV-4). Default false — the chip
     *  self-hides when no focus is set, so unconditional mount is safe
     *  on every surface and the SSOT `useFocusContext` decides
     *  visibility. Opt out only when a surface already renders its own
     *  focus chrome inline. */
    hideFocusChip?: boolean | undefined;
    /** Optional click handler for the whole header — used where the
     *  title bar doubles as a "scroll to top" affordance. */
    onHeaderClick?: (() => void) | undefined;
    /** Extra class on the wrapper for per-surface tweaks. */
    className?: string | undefined;
};

function SurfaceHeader({
    title,
    eyebrow,
    subtitle,
    icon,
    onMenuClick,
    showMenu = true,
    onBack,
    backLabel = 'Back',
    breadcrumb,
    count,
    actions,
    hideNotifications = false,
    hideFocusChip = false,
    onHeaderClick,
    className = '',
}: SurfaceHeaderProps) {
    const showHamburger = !onBack && showMenu && onMenuClick;

    return (
        <header
            className={`surface-header ${className}`.trim()}
            role="banner"
            {...(onHeaderClick ? { onClick: onHeaderClick } : {})}
        >
            {onBack ? (
                <button
                    type="button"
                    className="surface-header__back"
                    onClick={(e) => { e.stopPropagation(); onBack(); }}
                    aria-label={backLabel}
                    title={backLabel}
                >
                    <ArrowLeft size={18} />
                </button>
            ) : showHamburger ? (
                <button
                    type="button"
                    className="surface-header__menu"
                    onClick={(e) => { e.stopPropagation(); onMenuClick?.(); }}
                    aria-label="Open navigation"
                >
                    <PanelLeft size={18} />
                </button>
            ) : null}

            <div className="surface-header__title-group">
                {eyebrow && <span className="surface-header__eyebrow">{eyebrow}</span>}
                <div className="surface-header__title-row">
                    {icon && (
                        <span className="surface-header__icon" aria-hidden="true">
                            {icon}
                        </span>
                    )}
                    {breadcrumb && breadcrumb.length > 0 ? (
                        <Breadcrumb items={breadcrumb} headerTitle />
                    ) : (
                        <h2 className="surface-header__title">{title}</h2>
                    )}
                    {count != null && <span className="surface-header__count">{count}</span>}
                </div>
                {subtitle && <p className="surface-header__subtitle">{subtitle}</p>}
            </div>

            {(actions || !hideNotifications || !hideFocusChip) && (
                <div className="surface-header__actions">
                    {actions}
                    {/* FocusChip sits adjacent to NetworkIndicator +
                     *  NotificationsBell so the SSOT chrome row shows
                     *  the current focus on every surface, not just the
                     *  mentorship rail (where ContextSidebar mirrors the
                     *  same provider at ≥1280px). The chip self-hides
                     *  when no focus is set. */}
                    {!hideFocusChip && <FocusChip />}
                    {!hideNotifications && (
                        <>
                            <NetworkIndicator />
                            <NotificationsBell />
                        </>
                    )}
                </div>
            )}
        </header>
    );
}

export default SurfaceHeader;
